/**
 * GuidedView Context v2.4 • ROLE-AWARE DEMO CONDUCTOR
 * 
 * Manages the guided walkthrough with:
 * - ROLE-AWARE PERSONA VISIBILITY (only show valid personas for role)
 * - ONE-CLICK START (no restarts required)
 * - SEAMLESS PERSONA SWITCHING (continues playback, no restart)
 * - TRUE AUTO PLAYBACK (starts immediately on mode switch)
 * - AUTO-PAUSE on user interaction (resumes after 1s)
 * - 10-STEP role-aware flow with smart-skipping
 * - STATE PERSISTENCE with proper priority resolution
 * - AUTO-NAVIGATION to step routes
 * - Presentation mode flag for toast suppression
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  GUIDED_COPY_MATRIX,
  PERSONAS,
  PLAYBACK_MODES,
  PLAYBACK_SPEEDS,
  STEP_ORDER,
  STORAGE_KEYS,
  getStepCopy,
  getStepsForRole,
  getPersonasForRole,
  getDefaultPersonaForRole,
  canAccessPersona,
  shouldShowPersonaSelector,
  canAccessDeveloperGuide
} from '../guided/guidedCopyMatrix';

const GuidedViewContext = createContext(null);

// Safe localStorage helpers
const safeGetItem = (key, defaultValue) => {
  try {
    const value = localStorage.getItem(key);
    return value !== null ? value : defaultValue;
  } catch {
    return defaultValue;
  }
};

const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage not available
  }
};

export const GuidedViewProvider = ({ children, userRole = 'client_admin' }) => {
  // SESSION STATE (takes precedence over localStorage)
  const sessionPersonaRef = useRef(null);
  const sessionModeRef = useRef(null);
  const sessionSpeedRef = useRef(null);
  
  // Get allowed personas for this role (server-authoritative)
  const allowedPersonas = getPersonasForRole(userRole);
  const showPersonaSelector = shouldShowPersonaSelector(userRole);
  const canUseDeveloperGuide = canAccessDeveloperGuide(userRole);
  
  // State resolution priority:
  // 1. Current session choice (sessionRef) - but MUST be valid for role
  // 2. localStorage persisted value - but MUST be valid for role
  // 3. Role-based default
  // 4. System fallback
  
  const resolvePersona = useCallback(() => {
    // Check session choice first
    if (sessionPersonaRef.current && canAccessPersona(userRole, sessionPersonaRef.current)) {
      return sessionPersonaRef.current;
    }
    
    // Check localStorage
    const stored = safeGetItem(STORAGE_KEYS.PERSONA, null);
    if (stored && canAccessPersona(userRole, stored)) {
      return stored;
    }
    
    // Fall back to role-based default
    return getDefaultPersonaForRole(userRole);
  }, [userRole]);
  
  const resolveMode = useCallback(() => {
    if (sessionModeRef.current) return sessionModeRef.current;
    const stored = safeGetItem(STORAGE_KEYS.MODE, null);
    if (stored && Object.values(PLAYBACK_MODES).includes(stored)) return stored;
    return PLAYBACK_MODES.MANUAL;
  }, []);
  
  const resolveSpeed = useCallback(() => {
    if (sessionSpeedRef.current) return sessionSpeedRef.current;
    const stored = safeGetItem(STORAGE_KEYS.SPEED, null);
    if (stored && PLAYBACK_SPEEDS[stored]) return stored;
    return 'normal';
  }, []);

  // Initialize state
  const [isEnabled, setIsEnabled] = useState(() => 
    safeGetItem(STORAGE_KEYS.ENABLED, 'false') === 'true'
  );
  const [persona, setPersonaState] = useState(resolvePersona);
  const [mode, setModeState] = useState(resolveMode);
  const [speed, setSpeedState] = useState(resolveSpeed);
  const [loop, setLoop] = useState(() => 
    safeGetItem(STORAGE_KEYS.LOOP, 'false') === 'true'
  );
  
  // Compute available steps based on role
  const availableSteps = getStepsForRole(userRole);
  const totalSteps = availableSteps.length;
  
  // Step management
  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    const stored = parseInt(safeGetItem(STORAGE_KEYS.STEP_INDEX, '0'), 10);
    return Math.min(stored, totalSteps - 1);
  });
  
  // Overlay and autoplay state
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPausedByInteraction, setIsPausedByInteraction] = useState(false);
  
  // Refs for autoplay
  const autoplayTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Get current step ID and data
  const currentStepId = availableSteps[currentStepIndex];
  const currentStepData = getStepCopy(currentStepId, persona);

  // Validate persona on role change
  useEffect(() => {
    if (!canAccessPersona(userRole, persona)) {
      const validPersona = getDefaultPersonaForRole(userRole);
      setPersonaState(validPersona);
      sessionPersonaRef.current = validPersona;
    }
  }, [userRole, persona]);

  // Persist state changes to localStorage
  useEffect(() => {
    safeSetItem(STORAGE_KEYS.ENABLED, isEnabled.toString());
  }, [isEnabled]);

  useEffect(() => {
    safeSetItem(STORAGE_KEYS.PERSONA, persona);
  }, [persona]);

  useEffect(() => {
    safeSetItem(STORAGE_KEYS.MODE, mode);
  }, [mode]);

  useEffect(() => {
    safeSetItem(STORAGE_KEYS.STEP_INDEX, currentStepIndex.toString());
  }, [currentStepIndex]);

  useEffect(() => {
    safeSetItem(STORAGE_KEYS.SPEED, speed);
  }, [speed]);

  useEffect(() => {
    safeSetItem(STORAGE_KEYS.LOOP, loop.toString());
  }, [loop]);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, []);

  // Auto-navigate to step route when step changes
  useEffect(() => {
    if (isOpen && currentStepData?.targetPath) {
      const targetPath = currentStepData.targetPath;
      if (location.pathname !== targetPath) {
        navigate(targetPath);
      }
    }
  }, [isOpen, currentStepData?.targetPath, location.pathname, navigate]);

  // Start autoplay timer for current step
  const startAutoplayTimer = useCallback(() => {
    if (mode !== PLAYBACK_MODES.AUTO || !isPlaying || isPausedByInteraction) return;
    
    clearAllTimers();
    
    const duration = PLAYBACK_SPEEDS[speed]?.ms || PLAYBACK_SPEEDS.normal.ms;
    
    autoplayTimerRef.current = setTimeout(() => {
      if (currentStepIndex < totalSteps - 1) {
        setCurrentStepIndex(prev => prev + 1);
      } else if (loop) {
        // Loop back to start
        setCurrentStepIndex(0);
      } else {
        // End of guided view
        setIsPlaying(false);
      }
    }, duration);
  }, [mode, isPlaying, isPausedByInteraction, speed, currentStepIndex, totalSteps, loop, clearAllTimers]);

  // Effect to manage autoplay timer
  useEffect(() => {
    if (isOpen && mode === PLAYBACK_MODES.AUTO && isPlaying && !isPausedByInteraction) {
      startAutoplayTimer();
    } else {
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    }
    
    return () => {
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
      }
    };
  }, [isOpen, mode, isPlaying, isPausedByInteraction, currentStepIndex, startAutoplayTimer]);

  // Handle user interaction pause (for auto mode)
  // Auto-resume after 1 second unless explicitly paused
  const handleInteractionPause = useCallback(() => {
    if (mode === PLAYBACK_MODES.AUTO && isPlaying && !isPausedByInteraction) {
      setIsPausedByInteraction(true);
      clearAllTimers();
      
      // Auto-resume after 1 second
      resumeTimerRef.current = setTimeout(() => {
        setIsPausedByInteraction(false);
      }, 1000);
    }
  }, [mode, isPlaying, isPausedByInteraction, clearAllTimers]);

  // Toggle guided view ON (one-click start)
  const enableGuidedView = useCallback(() => {
    setIsEnabled(true);
    setIsOpen(true);
    setCurrentStepIndex(0);
    setIsPausedByInteraction(false);
    
    // If mode is AUTO, start playing immediately
    if (mode === PLAYBACK_MODES.AUTO) {
      setIsPlaying(true);
    }
  }, [mode]);

  // Toggle guided view OFF
  const disableGuidedView = useCallback(() => {
    setIsEnabled(false);
    setIsOpen(false);
    setIsPlaying(false);
    setIsPausedByInteraction(false);
    clearAllTimers();
  }, [clearAllTimers]);

  // Toggle (for backward compatibility)
  const toggleGuidedView = useCallback(() => {
    if (isEnabled) {
      disableGuidedView();
    } else {
      enableGuidedView();
    }
  }, [isEnabled, enableGuidedView, disableGuidedView]);

  // Start guided view (if already enabled but closed)
  const start = useCallback(() => {
    if (!isEnabled) {
      enableGuidedView();
    } else {
      setIsOpen(true);
      setCurrentStepIndex(0);
      setIsPausedByInteraction(false);
      
      if (mode === PLAYBACK_MODES.AUTO) {
        setIsPlaying(true);
      }
    }
  }, [isEnabled, enableGuidedView, mode]);

  // Close overlay (but keep enabled)
  const close = useCallback(() => {
    setIsOpen(false);
    setIsPlaying(false);
    setIsPausedByInteraction(false);
    clearAllTimers();
  }, [clearAllTimers]);

  // Navigate to next step
  const nextStep = useCallback(() => {
    // Pause auto and schedule resume
    if (mode === PLAYBACK_MODES.AUTO && isPlaying) {
      handleInteractionPause();
    }
    
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else if (loop) {
      setCurrentStepIndex(0);
    } else {
      // Finish
      setIsOpen(false);
      setIsPlaying(false);
    }
  }, [currentStepIndex, totalSteps, loop, mode, isPlaying, handleInteractionPause]);

  // Navigate to previous step
  const prevStep = useCallback(() => {
    // Pause auto and schedule resume
    if (mode === PLAYBACK_MODES.AUTO && isPlaying) {
      handleInteractionPause();
    }
    
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex, mode, isPlaying, handleInteractionPause]);

  // Go to specific step
  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < totalSteps) {
      setCurrentStepIndex(stepIndex);
    }
  }, [totalSteps]);

  // Change persona (SEAMLESS - keeps overlay open, continues playback)
  const changePersona = useCallback((newPersona) => {
    // Validate role can access this persona
    if (!canAccessPersona(userRole, newPersona)) {
      console.warn(`Role ${userRole} cannot access persona ${newPersona}`);
      return;
    }
    
    sessionPersonaRef.current = newPersona;
    setPersonaState(newPersona);
    
    // Pause auto mode briefly on persona change, then resume
    if (mode === PLAYBACK_MODES.AUTO && isPlaying) {
      handleInteractionPause();
    }
    
    // DO NOT close overlay or reset step - SEAMLESS switching
  }, [userRole, mode, isPlaying, handleInteractionPause]);

  // Change mode (AUTO starts immediately)
  const changeMode = useCallback((newMode) => {
    if (Object.values(PLAYBACK_MODES).includes(newMode)) {
      sessionModeRef.current = newMode;
      setModeState(newMode);
      setIsPausedByInteraction(false);
      clearAllTimers();
      
      // If switching to AUTO while overlay is open, start playing IMMEDIATELY
      if (newMode === PLAYBACK_MODES.AUTO && isOpen) {
        setIsPlaying(true);
      } else if (newMode === PLAYBACK_MODES.MANUAL) {
        setIsPlaying(false);
      }
    }
  }, [isOpen, clearAllTimers]);

  // Change speed
  const changeSpeed = useCallback((newSpeed) => {
    if (PLAYBACK_SPEEDS[newSpeed]) {
      sessionSpeedRef.current = newSpeed;
      setSpeedState(newSpeed);
    }
  }, []);

  // Toggle loop
  const toggleLoop = useCallback(() => {
    setLoop(prev => !prev);
  }, []);

  // Play (auto mode)
  const play = useCallback(() => {
    if (mode === PLAYBACK_MODES.AUTO) {
      setIsPlaying(true);
      setIsPausedByInteraction(false);
      clearAllTimers();
    }
  }, [mode, clearAllTimers]);

  // Pause (auto mode) - EXPLICIT pause, does not auto-resume
  const pause = useCallback(() => {
    if (mode === PLAYBACK_MODES.AUTO) {
      setIsPlaying(false);
      setIsPausedByInteraction(true);
      clearAllTimers();
      // Clear any pending resume timer
      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = null;
      }
    }
  }, [mode, clearAllTimers]);

  // Check if current route matches expected step
  const isOnExpectedRoute = currentStepData?.targetPath === location.pathname;

  // Presentation mode flag (for toast suppression)
  const isPresentationMode = isEnabled && isOpen;

  const value = {
    // State
    isEnabled,
    isOpen,
    persona,
    mode,
    speed,
    loop,
    currentStepIndex,
    totalSteps,
    availableSteps,
    currentStepData,
    currentStepId,
    isPlaying,
    isPausedByInteraction,
    isOnExpectedRoute,
    isPresentationMode, // For toast suppression
    
    // Role-aware persona visibility (v2.4)
    userRole,
    allowedPersonas,
    showPersonaSelector,
    canUseDeveloperGuide,
    
    // Constants
    PERSONAS,
    PLAYBACK_MODES,
    PLAYBACK_SPEEDS,
    STEP_ORDER,
    
    // Actions
    enableGuidedView,
    disableGuidedView,
    toggleGuidedView,
    start,
    close,
    nextStep,
    prevStep,
    goToStep,
    changePersona,
    changeMode,
    changeSpeed,
    toggleLoop,
    play,
    pause,
    handleInteractionPause,
    
    // Helpers
    getStepCopy,
    getStepsForRole,
    canAccessPersona: (p) => canAccessPersona(userRole, p)
  };

  return (
    <GuidedViewContext.Provider value={value}>
      {children}
    </GuidedViewContext.Provider>
  );
};

export const useGuidedView = () => {
  const context = useContext(GuidedViewContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      isEnabled: false,
      isOpen: false,
      isPresentationMode: false,
      allowedPersonas: [PERSONAS.CLIENT],
      showPersonaSelector: false,
      PERSONAS,
      PLAYBACK_MODES,
      PLAYBACK_SPEEDS
    };
  }
  return context;
};

// Re-export constants for convenience
export { PERSONAS, PLAYBACK_MODES, PLAYBACK_SPEEDS, STEP_ORDER };
